import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { X, Plus, Trash2, GripVertical, Eye, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OGImageSelector } from "./OGImageSelector";

const T = {
  navy: "#002868",
  red: "#B22234",
  gold: "#C5A55A",
  border: "rgba(27,27,27,0.12)",
};
const serif = "'Instrument Serif', Georgia, serif";
const sans = "'Instrument Sans', system-ui, sans-serif";

interface Props {
  post: any | null; // null = creating new
  onClose: () => void;
  onSaved: () => void;
}

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

const CONTENT_TYPES = [
  { value: "article", label: "Article" },
  { value: "data_insight", label: "Data Insight" },
  { value: "news_commentary", label: "News Commentary" },
  { value: "state_law_analysis", label: "State Law Analysis" },
  { value: "hospital_spotlight", label: "Hospital Spotlight" },
  { value: "cost_comparison", label: "Cost Comparison" },
  { value: "how_to_guide", label: "How-To Guide" },
];

const STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "pending_approval", label: "Pending Approval" },
  { value: "approved", label: "Approved" },
  { value: "published", label: "Published" },
  { value: "denied", label: "Denied" },
  { value: "archived", label: "Archived" },
];

export function BlogPostDialog({ post, onClose, onSaved }: Props) {
  const isEditing = !!post;
  const [tab, setTab] = useState<"edit" | "preview">("edit");

  // Form state
  const [title, setTitle] = useState(post?.title || "");
  const [slug, setSlug] = useState(post?.slug || "");
  const [stateCode, setStateCode] = useState(post?.state_code || "");
  const [contentType, setContentType] = useState(post?.content_type || "article");
  const [brandKey, setBrandKey] = useState(post?.brand_key || "ahp");
  const [status, setStatus] = useState(post?.status || "draft");
  const [audience, setAudience] = useState(post?.audience || "");
  const [heroIcon, setHeroIcon] = useState(post?.hero_icon || "Shield");
  const [readTime, setReadTime] = useState(post?.read_time || "");
  const [excerpt, setExcerpt] = useState(post?.excerpt || "");

  // SEO
  const [metaTitle, setMetaTitle] = useState(post?.meta_title || "");
  const [metaDescription, setMetaDescription] = useState(post?.meta_description || "");
  const [keywords, setKeywords] = useState(post?.keywords || "");
  const [ogImageUrl, setOgImageUrl] = useState(post?.og_image_url || "");

  // Sections
  const [sections, setSections] = useState<{ heading: string; content: string }[]>(
    post?.sections || [{ heading: "", content: "" }]
  );

  // CTA
  const [ctaHeading, setCtaHeading] = useState(post?.cta?.heading || "");
  const [ctaText, setCtaText] = useState(post?.cta?.text || "");
  const [ctaButton, setCtaButton] = useState(post?.cta?.buttonText || "Learn More");
  const [ctaLink, setCtaLink] = useState(post?.cta?.buttonLink || "/for-business");

  // Auto-generate slug from title
  const handleTitleChange = (val: string) => {
    setTitle(val);
    if (!isEditing || !post?.slug) {
      setSlug(slugify(val));
    }
  };

  const addSection = () => setSections([...sections, { heading: "", content: "" }]);
  const removeSection = (i: number) => setSections(sections.filter((_, idx) => idx !== i));
  const updateSection = (i: number, field: "heading" | "content", val: string) => {
    const next = [...sections];
    next[i] = { ...next[i], [field]: val };
    setSections(next);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title,
        slug,
        state_code: stateCode || null,
        content_type: contentType,
        brand_key: brandKey,
        status,
        audience: audience || null,
        hero_icon: heroIcon,
        read_time: readTime || null,
        excerpt: excerpt || null,
        meta_title: metaTitle || null,
        meta_description: metaDescription || null,
        keywords: keywords || null,
        og_image_url: ogImageUrl || null,
        sections: JSON.stringify(sections),
        cta: JSON.stringify({
          heading: ctaHeading,
          text: ctaText,
          buttonText: ctaButton,
          buttonLink: ctaLink,
        }),
        updated_at: new Date().toISOString(),
        ...(status === "published" && !post?.publish_date
          ? { publish_date: new Date().toISOString() }
          : {}
        ),
      };

      if (isEditing) {
        const { error } = await supabase
          .from("blog_posts" as any)
          .update(payload as any)
          .eq("id", post.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("blog_posts" as any)
          .insert(payload as any);
        if (error) throw error;
      }
    },
    onSuccess: onSaved,
  });

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 12px",
    border: `1px solid ${T.border}`,
    borderRadius: 6,
    fontSize: 13,
    fontFamily: sans,
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: "rgba(27,27,27,0.62)",
    fontFamily: sans,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 4,
    display: "block",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 999,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      backgroundColor: "rgba(0,0,0,0.4)",
      overflowY: "auto", padding: "40px 16px",
    }}>
      <div style={{
        backgroundColor: "#fff", borderRadius: 12,
        width: 820, maxWidth: "100%", boxShadow: "0 16px 48px rgba(0,0,0,0.2)",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 24px", borderBottom: `1px solid ${T.border}`,
        }}>
          <h2 style={{ fontFamily: serif, fontSize: 20, color: T.navy }}>
            {isEditing ? "Edit Blog Post" : "New Blog Post"}
          </h2>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {/* Edit/Preview toggle */}
            <div style={{
              display: "flex", gap: 2, backgroundColor: "rgba(0,0,0,0.04)",
              borderRadius: 6, padding: 2,
            }}>
              {(["edit", "preview"] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  style={{
                    padding: "4px 12px", border: "none", borderRadius: 4,
                    backgroundColor: tab === t ? "#fff" : "transparent",
                    color: tab === t ? T.navy : "rgba(27,27,27,0.4)",
                    fontSize: 12, fontWeight: tab === t ? 600 : 400,
                    fontFamily: sans, cursor: "pointer",
                    boxShadow: tab === t ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  {t === "edit" ? "Edit" : "Preview"}
                </button>
              ))}
            </div>
            <button
              onClick={onClose}
              style={{ padding: 4, border: "none", background: "none", cursor: "pointer", color: "#999" }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: 24, maxHeight: "70vh", overflowY: "auto" }}>
          {tab === "edit" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {/* Row: title + slug */}
              <div>
                <label style={labelStyle}>Title</label>
                <input
                  value={title}
                  onChange={e => handleTitleChange(e.target.value)}
                  style={inputStyle}
                  placeholder="Blog post title..."
                />
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Slug</label>
                  <input value={slug} onChange={e => setSlug(e.target.value)} style={inputStyle} />
                </div>
                <div style={{ width: 100 }}>
                  <label style={labelStyle}>State</label>
                  <input value={stateCode} onChange={e => setStateCode(e.target.value.toUpperCase())} style={inputStyle} placeholder="TX" maxLength={2} />
                </div>
              </div>

              {/* Row: type, brand, status, icon */}
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Content Type</label>
                  <select value={contentType} onChange={e => setContentType(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                    {CONTENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                    {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div style={{ width: 80 }}>
                  <label style={labelStyle}>Brand</label>
                  <select value={brandKey} onChange={e => setBrandKey(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                    <option value="ahp">AHP</option>
                    <option value="luigis">Luigi's</option>
                  </select>
                </div>
              </div>

              {/* Row: audience, icon, read time */}
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 2 }}>
                  <label style={labelStyle}>Audience</label>
                  <input value={audience} onChange={e => setAudience(e.target.value)} style={inputStyle} placeholder="e.g., Medical Bill Advocates" />
                </div>
                <div style={{ width: 120 }}>
                  <label style={labelStyle}>Hero Icon</label>
                  <input value={heroIcon} onChange={e => setHeroIcon(e.target.value)} style={inputStyle} placeholder="Shield" />
                </div>
                <div style={{ width: 120 }}>
                  <label style={labelStyle}>Read Time</label>
                  <input value={readTime} onChange={e => setReadTime(e.target.value)} style={inputStyle} placeholder="5 min read" />
                </div>
              </div>

              {/* Excerpt */}
              <div>
                <label style={labelStyle}>Excerpt</label>
                <textarea value={excerpt} onChange={e => setExcerpt(e.target.value)} rows={2} style={{ ...inputStyle, resize: "vertical" }} placeholder="1-2 sentence summary for cards and social..." />
              </div>

              {/* Sections */}
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Sections</label>
                  <button
                    onClick={addSection}
                    style={{
                      display: "flex", alignItems: "center", gap: 4,
                      padding: "4px 10px", border: `1px solid ${T.border}`,
                      borderRadius: 6, backgroundColor: "#fff", fontSize: 11,
                      fontFamily: sans, cursor: "pointer", color: T.navy,
                    }}
                  >
                    <Plus size={12} /> Add Section
                  </button>
                </div>
                {sections.map((sec, i) => (
                  <div
                    key={i}
                    style={{
                      marginBottom: 12, padding: 16,
                      border: `1px solid ${T.border}`, borderRadius: 8,
                      backgroundColor: "rgba(250,249,246,0.5)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <GripVertical size={14} style={{ color: "rgba(27,27,27,0.2)" }} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(27,27,27,0.4)", fontFamily: sans }}>
                        Section {i + 1}
                      </span>
                      {sections.length > 1 && (
                        <button
                          onClick={() => removeSection(i)}
                          style={{
                            marginLeft: "auto", padding: 2, border: "none",
                            background: "none", cursor: "pointer", color: T.red,
                          }}
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                    <input
                      value={sec.heading}
                      onChange={e => updateSection(i, "heading", e.target.value)}
                      style={{ ...inputStyle, marginBottom: 8, fontWeight: 600 }}
                      placeholder="Section heading..."
                    />
                    <textarea
                      value={sec.content}
                      onChange={e => updateSection(i, "content", e.target.value)}
                      rows={5}
                      style={{ ...inputStyle, resize: "vertical", fontFamily: "monospace", fontSize: 12 }}
                      placeholder="HTML content..."
                    />
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div style={{
                padding: 16, border: `1px solid ${T.border}`, borderRadius: 8,
                backgroundColor: "rgba(0,40,104,0.02)",
              }}>
                <label style={{ ...labelStyle, marginBottom: 12 }}>Call to Action</label>
                <div style={{ display: "flex", gap: 12, marginBottom: 8 }}>
                  <input value={ctaHeading} onChange={e => setCtaHeading(e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="CTA heading..." />
                  <input value={ctaButton} onChange={e => setCtaButton(e.target.value)} style={{ ...inputStyle, width: 140 }} placeholder="Button text" />
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <input value={ctaText} onChange={e => setCtaText(e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="CTA body text..." />
                  <input value={ctaLink} onChange={e => setCtaLink(e.target.value)} style={{ ...inputStyle, width: 180 }} placeholder="/for-business" />
                </div>
              </div>

              {/* SEO */}
              <div style={{
                padding: 16, border: `1px solid ${T.border}`, borderRadius: 8,
                backgroundColor: "rgba(0,0,0,0.01)",
              }}>
                <label style={{ ...labelStyle, marginBottom: 12 }}>SEO</label>
                <input value={metaTitle} onChange={e => setMetaTitle(e.target.value)} style={{ ...inputStyle, marginBottom: 8 }} placeholder="Meta title..." />
                <textarea value={metaDescription} onChange={e => setMetaDescription(e.target.value)} rows={2} style={{ ...inputStyle, resize: "vertical", marginBottom: 8 }} placeholder="Meta description..." />
                <input value={keywords} onChange={e => setKeywords(e.target.value)} style={{ ...inputStyle, marginBottom: 16 }} placeholder="Keywords (comma-separated)..." />

                {/* OG Image — AI generation or manual URL */}
                {isEditing && post?.id ? (
                  <OGImageSelector
                    postId={post.id}
                    title={title}
                    excerpt={excerpt}
                    contentType={contentType}
                    currentOgImageUrl={ogImageUrl}
                    onImageSelected={(url) => setOgImageUrl(url)}
                  />
                ) : (
                  <div>
                    <label style={labelStyle}>OG Image URL</label>
                    <input
                      value={ogImageUrl}
                      onChange={e => setOgImageUrl(e.target.value)}
                      style={inputStyle}
                      placeholder="https://... (save post first to use AI generation)"
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* PREVIEW TAB */
            <div style={{ maxWidth: 640, margin: "0 auto" }}>
              <h1 style={{ fontFamily: serif, fontSize: 28, color: T.navy, marginBottom: 16 }}>
                {title || "Untitled Post"}
              </h1>
              <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
                {audience && (
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 12,
                    backgroundColor: "rgba(178,34,52,0.1)", color: T.red, fontFamily: sans,
                  }}>
                    {audience}
                  </span>
                )}
                {stateCode && (
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 12,
                    backgroundColor: "rgba(197,165,90,0.15)", color: "#8B7730", fontFamily: sans,
                  }}>
                    {stateCode}
                  </span>
                )}
                {readTime && (
                  <span style={{ fontSize: 11, color: "#999", fontFamily: sans }}>{readTime}</span>
                )}
              </div>
              {sections.map((sec, i) => (
                <div key={i} style={{ marginBottom: 24 }}>
                  <h2 style={{ fontFamily: serif, fontSize: 20, color: T.navy, marginBottom: 8 }}>
                    {sec.heading || `Section ${i + 1}`}
                  </h2>
                  <div
                    style={{ fontFamily: sans, fontSize: 14, color: "#333", lineHeight: 1.7 }}
                    dangerouslySetInnerHTML={{ __html: sec.content || "<p><em>No content yet...</em></p>" }}
                  />
                </div>
              ))}
              {ctaHeading && (
                <div style={{
                  marginTop: 32, padding: 24, borderRadius: 12, textAlign: "center",
                  backgroundColor: "rgba(0,40,104,0.05)", border: `1px solid ${T.border}`,
                }}>
                  <h3 style={{ fontFamily: serif, fontSize: 20, color: T.navy, marginBottom: 8 }}>{ctaHeading}</h3>
                  <p style={{ fontFamily: sans, fontSize: 14, color: "#666", marginBottom: 16 }}>{ctaText}</p>
                  <Button style={{ backgroundColor: T.navy, color: "#fff", fontFamily: sans }}>
                    {ctaButton}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 8,
          padding: "16px 24px", borderTop: `1px solid ${T.border}`,
        }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!title || !slug || saveMutation.isPending}
            style={{
              backgroundColor: T.navy, color: "#fff",
              fontFamily: sans, opacity: (!title || !slug) ? 0.5 : 1,
            }}
          >
            <Save size={14} />
            {saveMutation.isPending ? "Saving..." : isEditing ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}
